import {
    AddAttachmentDto,
    AddCustomerImpactDto,
    AddWatcherDto,
    AssignRequestDto,
    AttachmentDto,
    CreateProposalDto,
    CreateQuestionDto,
    CreateRfxDto,
    CustomerImpactDto,
    EditProposalDto,
    EditQuestionDto,
    EntityType,
    HashTagDto,
    IAddAttachmentDto,
    IAddCustomerImpactDto,
    IAssignRequestDto,
    ICreateProposalDto,
    ICreateQuestionDto,
    ICreateRfxDto,
    ICustomerImpactDto,
    IHashTagDto,
    IUpdateClassificationDto,
    RequestType,
    SdsgApiClient,
    UpdateClassificationDto,
    UserDto
} from "generated/sdsg-backend";
import { IGetAttachment, IShareRequest } from "app/models/common/request";
import { API, RouteConstants } from "app/utils/constants";
import { useQuery } from "react-query";
import { RequestDto } from "generated/request-backend";

// The local environment has a trailing slash in the API_SERVER_URL, but test and prod do not.
const baseUrl : string = API.API_SERVER_URL.endsWith('/') ? API.API_SERVER_URL.slice(0,-1) : API.API_SERVER_URL;

//#region #Queries
export const useGetAttachments = (requestData: IGetAttachment) => {
    const sdsgApi : SdsgApiClient = new SdsgApiClient(baseUrl);
    return useQuery(
        ['useGetAttachments'],
        async () => {
            if (requestData) {
                switch (requestData.requestType) {
                    case 1: // Proposal
                        return await sdsgApi.proposal_GetAttachments(requestData.id, requestData.entityType, requestData.portal);
                    case 2: // Question
                        return await sdsgApi.question_GetAttachments(requestData.id, requestData.entityType, requestData.portal);
                    case 3: // Rfx
                        return await sdsgApi.rfx_GetAttachments(requestData.id, requestData.entityType, requestData.portal);                
                    default:
                        break;
                }
            }
        },
        { cacheTime: 0 }
    );
};
//#endregion
//#region Mutations
/**
 * Mutation used to submit shared request from unified dashboard
 */
export const useShareRequest = (data: IShareRequest) => {
    const sdsgApi : SdsgApiClient = new SdsgApiClient(baseUrl);
    
    const users : UserDto[] = [];
    data.users.map(user => users.push(new UserDto({...user})));

    const attachments : AttachmentDto[] = [];
    data.attachments.map(attachment => attachments.push(new AttachmentDto({...attachment})));
    
    const dataDto : AddWatcherDto = new AddWatcherDto({
        ...data,
        users: users,
        attachments: attachments
    });

    switch (data.requestType) {
        case RequestType.Proposal:
            return sdsgApi.proposal_UpdateWatcher(dataDto);
        case RequestType.Question:
            return sdsgApi.question_UpdateWatcher(dataDto);
        case RequestType.Rfx:
            return sdsgApi.rfx_UpdateWatcher(dataDto);
        default:
            // Error
            break;
    }
};

/**
 * Mutation used to submit updated request from unified dashboard
 */
export const useUpdateRequest = (data: RequestDto) => {
    const sdsgApi : SdsgApiClient = new SdsgApiClient(baseUrl);

    const dataInterface = {
        ...data,
        // requestedResolutionDate: data.requestedResolutionDate,
        // companyInformation: new CompanyInformationDto({ ...data.companyInformation })
    }

    switch (data.requestType) {
        case RequestType.Proposal:
            // Since EditProposalDto extends EditRequestDto, we want to create the dto and then init it in order to include extended fields. 
            const proposalDto : EditProposalDto = new EditProposalDto();
            proposalDto.init(dataInterface);
            return sdsgApi.proposal_UpdateProposal(proposalDto);
        case RequestType.Question:
            // Since EditQuestionDto extends EditRequestDto, we want to create the dto and then init it in order to include extended fields.
            const questionDto : EditQuestionDto = new EditQuestionDto();
            questionDto.init(dataInterface);
            return sdsgApi.question_UpdateQuestion(questionDto);
        default:
            // Error
            break;
    }
};

/**
 * Module: Admin Assign Request
 * Custom hook to assign a request */
export const useAssignRequest = (requestType: RequestType, data: IAssignRequestDto) => {
    const sdsgApi : SdsgApiClient = new SdsgApiClient(baseUrl);
    const assignDto = new AssignRequestDto(data);

    switch (requestType) {
        case RequestType.Proposal:
            return sdsgApi.proposal_Assign(assignDto);
        case RequestType.Question:
            return sdsgApi.question_Assign(assignDto);
        case RequestType.Rfx:
            return sdsgApi.rfx_Assign(assignDto);
        default:
            // Error
            break;
    }
};

/**
 * Module: Create Request
 * Used for creating new requests (e.g., aggregate parent creation) */
export const useCreateRequest = (requestType: RequestType, data: ICreateProposalDto | ICreateQuestionDto | ICreateRfxDto) => {
    const sdsgApi : SdsgApiClient = new SdsgApiClient(baseUrl);

    switch (requestType) {
        case RequestType.Proposal:
            const proposalDto : CreateProposalDto = new CreateProposalDto();
            proposalDto.init(data);
            return sdsgApi.proposal_CreateProposal(proposalDto);
        case RequestType.Question:
            const questionDto : CreateQuestionDto = new CreateQuestionDto();
            questionDto.init(data);
            return sdsgApi.question_CreateQuestion(questionDto);
        case RequestType.Rfx:
            const rfxDto : CreateRfxDto = new CreateRfxDto();
            rfxDto.init(data);
            return sdsgApi.rfx_CreateRfx(rfxDto);
        default:
            // Error
            break;
    }
};

export const usePostAddCustomer = async (route: RouteConstants, data: IAddCustomerImpactDto) => {
    const sdsgApi : SdsgApiClient = new SdsgApiClient(baseUrl);
    switch (route) {
        case RouteConstants.SDSG_PROPOSAL_ADDCUSTOMERIMPACT:
            return await sdsgApi.proposal_AddCustomerImpact(new AddCustomerImpactDto(data));
        case RouteConstants.SDSG_QUESTION_ADDCUSTOMERIMPACT:
            return await sdsgApi.question_AddCustomerImpact(new AddCustomerImpactDto(data));
        case RouteConstants.SDSG_RFX_ADDCUSTOMERIMPACT:
            return await sdsgApi.rfx_AddCustomerImpact(new AddCustomerImpactDto(data));
        default:
            // Error
            break;
    }
};

export const useAddAttachment = async (route: RouteConstants, data: IAddAttachmentDto) => {
    const sdsgApi : SdsgApiClient = new SdsgApiClient(baseUrl);
    switch (route) {
        case `${RouteConstants.SDSG_PROPOSAL}`:
            return await sdsgApi.proposal_AddAttachments(new AddAttachmentDto(data));
        case `${RouteConstants.SDSG_QUESTION}`:
            return await sdsgApi.question_AddAttachments(new AddAttachmentDto(data));
        case `${RouteConstants.SDSG_RFX}`:
            return await sdsgApi.rfx_AddAttachments(new AddAttachmentDto(data));
        default:
            // Error
            break;
    }
};

export const useRemoveAttachment = async (route: RouteConstants, id:string, entityId?:string, entityType?:EntityType) => {
    const sdsgApi : SdsgApiClient = new SdsgApiClient(baseUrl);
    switch (route) {
        case `${RouteConstants.SDSG_PROPOSAL}`:
            return await sdsgApi.proposal_DeleteAttachmentById(id, entityId, entityType);
        case `${RouteConstants.SDSG_QUESTION}`:
            return await sdsgApi.question_DeleteAttachmentById(id, entityId, entityType);
        case `${RouteConstants.SDSG_RFX}`:
            return await sdsgApi.rfx_DeleteAttachmentById(id, entityId, entityType);
        default:
            // Error
            break;
    }
};

export const useUpdateAttachmentClassification = async (route: RouteConstants, data: IUpdateClassificationDto) => {
    const sdsgApi : SdsgApiClient = new SdsgApiClient(baseUrl);
    switch (route) {
        // No route for updating attachments on proposals currently exists
        case `${RouteConstants.SDSG_QUESTION}`:
            return await sdsgApi.question_UpdateClassification(new UpdateClassificationDto(data));
        case `${RouteConstants.SDSG_RFX}`:
            return await sdsgApi.rfx_UpdateClassification(new UpdateClassificationDto(data));
        default:
            // Error
            break;
    }
};

export const useRemoveCustomerImpact = async (route: RouteConstants, id: string, data: ICustomerImpactDto) => {
    const sdsgApi : SdsgApiClient = new SdsgApiClient(baseUrl);
    switch (route) {
        case `${RouteConstants.SDSG_PROPOSAL}`:
            return await sdsgApi.proposal_DeleteCustomerImpact(id, new CustomerImpactDto(data));
        case `${RouteConstants.SDSG_QUESTION}`:
            return await sdsgApi.question_DeleteCustomerImpact(id, new CustomerImpactDto(data));
        case `${RouteConstants.SDSG_RFX}`:
            return await sdsgApi.rfx_DeleteCustomerImpact(id, new CustomerImpactDto(data));
        default:
            // Error
            break;
    }
};

export const usePostCommentsHashTags = async (route: RouteConstants, id: string, data: IHashTagDto[]) => {
    const sdsgApi : SdsgApiClient = new SdsgApiClient(baseUrl);
    const hashTags : HashTagDto[] = data.map(hashTag => {
        return new HashTagDto(hashTag);
    });

    switch (route) {
        case `${RouteConstants.SDSG_PROPOSAL}`:
            return await sdsgApi.proposal_AddHashTags(id, hashTags);
        case `${RouteConstants.SDSG_QUESTION}`:
            return await sdsgApi.question_AddHashTags(id, hashTags);
        case `${RouteConstants.SDSG_RFX}`:
            return await sdsgApi.rfx_AddHashTags(id, hashTags);
        default:
            // Error
            break;
    }
};
// usePostAddCustomer
//#endregion